import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Select,
  Checkbox,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  updateWorkSecurityRole,
  fetchWorkSecurityGroups,
  WorkSecurityGroup,
} from "services/work-security.service";
import { WorkSecurityArea } from "services/work-security-areas.service";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import AreaFormField, {
  defaultAreaSelection,
} from "features/work-security-areas/AreaFormField";
import { resolveAreaErrorMessage } from "features/work-security-areas/area-errors";

interface WorkSecurityRoleEditModalProps {
  roleId: string;
  initialName: string;
  initialDescription?: string;
  initialGroupId?: string | null;
  /** Accessible spheres (PR-2 §P4) — a Select to move the role is only offered in "all spheres" mode. */
  areas?: WorkSecurityArea[];
  /** The module header's concretely-selected sphere; null/undefined = "all spheres" mode. */
  selectedArea?: WorkSecurityArea | null;
  /** The role's own current sphere id — always shown/pre-selected regardless of header scope. */
  initialAreaId?: string | null;
  initialRequiredAtHiring?: boolean;
  onSuccess?: () => void;
}

export default function WorkSecurityRoleEditModal({
  roleId,
  initialName,
  initialDescription = "",
  initialGroupId = null,
  areas = [],
  selectedArea,
  initialAreaId = null,
  initialRequiredAtHiring = true,
  onSuccess,
}: WorkSecurityRoleEditModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [roleName, setRoleName] = useState(initialName || "");
  const [roleDescription, setRoleDescription] = useState(initialDescription || "");
  const [groups, setGroups] = useState<WorkSecurityGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState(initialGroupId || "");
  // Only meaningful in "all spheres" mode — see AreaFormField/showAreaField below.
  const [areaSelectValue, setAreaSelectValue] = useState(
    initialAreaId || defaultAreaSelection(areas)
  );
  const [requiredAtHiring, setRequiredAtHiring] = useState(
    initialRequiredAtHiring !== false
  );
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const showAreaField = areas.length > 1;
  // The header has a concrete sphere selected: show the role's OWN sphere
  // read-only (not necessarily `selectedArea` itself — a role reached via a
  // stale/foreign `?area=` link must still show the truth, not the header's
  // possibly-mismatched selection). In "all spheres" mode this stays null,
  // which is AreaFormField's signal to render the (Master-only-effective)
  // Select instead, defaulting to the role's own sphere.
  const roleOwnArea = selectedArea
    ? areas.find((area) => area.id === initialAreaId) || null
    : null;

  useEffect(() => {
    if (!companyId) return;
    // Only the sphere's own groups: update-role rejects a role/group pair from
    // two different spheres, so offering them here would be a trap.
    fetchWorkSecurityGroups(
      companyId,
      areaSelectValue ? { areaId: areaSelectValue } : undefined
    )
      .then((data) => {
        setGroups(data.groups || []);
        setGroupsLoaded(true);
      })
      .catch((err) => console.error("Failed to load work security groups", err));
  }, [companyId, areaSelectValue]);

  // Moving the role to another sphere leaves the previously picked group
  // behind: update-role refuses that pair, so drop it rather than let the save
  // fail. Waits for the list to arrive so the role's own group survives the
  // first render, when `groups` is still empty.
  useEffect(() => {
    if (!groupsLoaded) return;
    if (selectedGroup && !groups.some((group) => group.id === selectedGroup)) {
      setSelectedGroup("");
    }
  }, [groupsLoaded, groups, selectedGroup]);

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleFormSubmit = async () => {
    if (!companyId || !roleId) return;
    if (!roleName.trim()) {
      setError(t("role_name_required", { defaultValue: "Role name is required" }));
      return;
    }
    // Belt-and-suspenders (see WorkSecurityRoleFormModal): the Select is
    // always pre-filled, so this only guards a hypothetically empty `areas`.
    if (showAreaField && !selectedArea && !areaSelectValue) {
      setError(
        t("area_field_required", { defaultValue: "Please choose an instruction sphere." })
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      await updateWorkSecurityRole(companyId, roleId, {
        name: roleName.trim(),
        description: roleDescription.trim(),
        group: selectedGroup || null,
        // A sphere change is only ever offered via the Select in "all
        // spheres" mode; the read-only plaque never sends `area` at all, so
        // a non-Master editing a role scoped to a single concrete sphere
        // can never trip the backend's Master-only reassignment gate.
        ...(showAreaField && !selectedArea ? { area: areaSelectValue } : {}),
        requiredAtHiring,
      });

      close();
      if (onSuccess) onSuccess();
    } catch (err: any) {
      console.error("Failed to update role", err);
      setError(
        resolveAreaErrorMessage(err, t) ||
          err?.response?.data?.message ||
          t("failed_update_role", { defaultValue: "Failed to update role." })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}

        <TextField
          label={t("role_name", { defaultValue: "Role Name" })}
          value={roleName}
          onChange={setRoleName}
          placeholder={t("role_name_placeholder", {
            defaultValue: "e.g., Warehouse Operator",
          })}
          disabled={saving}
          autoComplete="off"
          requiredIndicator
        />

        <TextField
          label={t("role_description", { defaultValue: "Description" })}
          value={roleDescription}
          onChange={setRoleDescription}
          placeholder={t("role_desc_placeholder", {
            defaultValue: "Brief description of the role requirements...",
          })}
          disabled={saving}
          multiline={3}
          autoComplete="off"
        />

        <Select
          label={t("group", { defaultValue: "Group" })}
          options={[
            { label: t("no_group", { defaultValue: "No group" }), value: "" },
            ...groups.map((group) => ({ label: group.name, value: group.id })),
          ]}
          value={selectedGroup}
          onChange={setSelectedGroup}
          disabled={saving}
        />

        <AreaFormField
          areas={areas}
          selectedArea={roleOwnArea}
          value={areaSelectValue}
          onChange={setAreaSelectValue}
          disabled={saving}
        />

        <Checkbox
          label={t("work_security_required_at_hiring", {
            defaultValue: "Privaloma įdarbinant",
          })}
          helpText={t("work_security_required_at_hiring_hint", {
            defaultValue:
              "Šios instrukcijos nepasirašius, darbuotojo nepavyks įdarbinti.",
          })}
          checked={requiredAtHiring}
          onChange={setRequiredAtHiring}
          disabled={saving}
        />

        <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
          <Button onClick={close} disabled={saving}>
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="primary"
            onClick={handleFormSubmit}
            loading={saving}
            disabled={!roleName.trim()}
          >
            {t("save", { defaultValue: "Save" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
